import React, { useState, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Paper,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import LinkIcon from "@mui/icons-material/Link";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

/**
 * ImageUploader - Upload images via file selection or URL
 * All images are uploaded to FileBucket for consistent storage
 *
 * @param {string} value - Current image URL (FileBucket URL)
 * @param {function} onChange - Callback with new FileBucket URL
 * @param {string} imageVisibility - "public" or "private" (default: "private")
 * @param {string} label - Label for the field
 * @param {string} helperText - Helper text below the field
 */
function ImageUploader({
  value = "",
  onChange,
  imageVisibility = "private",
  label = "Image",
  helperText = "",
}) {
  const [mode, setMode] = useState("upload"); // "upload" or "url"
  const [urlInput, setUrlInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  /**
   * Upload file to FileBucket
   */
  const uploadToFileBucket = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("is_public", imageVisibility === "public" ? "true" : "false");

    const response = await fetch("/api3.2/bucket/upload", {
      method: "POST",
      credentials: "include",
      body: formData,
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `Upload failed: ${response.status}`);
    }

    const data = await response.json();

    if (imageVisibility === "public") {
      return `/api3.2/noauth/bucket/${data.id}`;
    }
    return `/api3.2/bucket/${data.id}`;
  };

  /**
   * Fetch image from URL and upload to FileBucket
   */
  const uploadFromUrl = async (url) => {
    // Fetch the image
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }

    const blob = await response.blob();

    // Extract filename from URL or use default
    const urlPath = new URL(url).pathname;
    const filename = urlPath.split("/").pop() || `image-${Date.now()}.jpg`;

    // Create a File object
    const file = new File([blob], filename, { type: blob.type || "image/jpeg" });

    // Upload to FileBucket
    return uploadToFileBucket(file);
  };

  /**
   * Handle file selection
   */
  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError("Image must be less than 10MB");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const url = await uploadToFileBucket(file);
      onChange(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle URL submission
   */
  const handleUrlSubmit = async () => {
    if (!urlInput.trim()) {
      setError("Please enter a URL");
      return;
    }

    // Basic URL validation
    try {
      new URL(urlInput);
    } catch {
      setError("Please enter a valid URL");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const url = await uploadFromUrl(urlInput);
      onChange(url);
      setUrlInput("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle file input change
   */
  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  /**
   * Handle drag and drop
   */
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  /**
   * Clear the current image
   */
  const handleClear = () => {
    onChange("");
    setUrlInput("");
    setError(null);
  };

  /**
   * Handle mode change
   */
  const handleModeChange = (e, newMode) => {
    if (newMode) {
      setMode(newMode);
      setError(null);
    }
  };

  return (
    <Box>
      {label && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {label}
        </Typography>
      )}

      {/* Show preview if we have an image */}
      {value ? (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            component="img"
            src={value}
            alt="Preview"
            sx={{
              width: 120,
              height: 80,
              objectFit: "cover",
              borderRadius: 1,
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
              <CheckCircleIcon color="success" fontSize="small" />
              <Typography variant="body2" color="success.main">
                Image uploaded
              </Typography>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {value}
            </Typography>
          </Box>
          <IconButton onClick={handleClear} color="error" title="Remove image">
            <DeleteIcon />
          </IconButton>
        </Paper>
      ) : (
        <Box>
          {/* Mode toggle */}
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={handleModeChange}
            size="small"
            sx={{ mb: 2 }}
          >
            <ToggleButton value="upload">
              <CloudUploadIcon sx={{ mr: 1 }} fontSize="small" />
              Upload File
            </ToggleButton>
            <ToggleButton value="url">
              <LinkIcon sx={{ mr: 1 }} fontSize="small" />
              From URL
            </ToggleButton>
          </ToggleButtonGroup>

          {mode === "upload" ? (
            /* File upload mode */
            <Paper
              variant="outlined"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                p: 3,
                textAlign: "center",
                cursor: "pointer",
                bgcolor: dragOver ? "action.hover" : "background.paper",
                borderStyle: "dashed",
                borderColor: dragOver ? "primary.main" : "divider",
                transition: "all 0.2s",
                "&:hover": {
                  bgcolor: "action.hover",
                  borderColor: "primary.main",
                },
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleInputChange}
                style={{ display: "none" }}
              />
              {loading ? (
                <CircularProgress size={40} />
              ) : (
                <>
                  <CloudUploadIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
                  <Typography variant="body1" gutterBottom>
                    Drag and drop an image here
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    or click to browse
                  </Typography>
                </>
              )}
            </Paper>
          ) : (
            /* URL mode */
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="https://example.com/image.jpg"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                disabled={loading}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleUrlSubmit();
                  }
                }}
              />
              <Button
                variant="contained"
                onClick={handleUrlSubmit}
                disabled={loading || !urlInput.trim()}
                sx={{ minWidth: 100 }}
              >
                {loading ? <CircularProgress size={20} /> : "Upload"}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Helper text */}
      {helperText && !error && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
}

export default ImageUploader;
